// The slice of ScrollMagic the templates used (scenes with a trigger element, triggerHook, offset,
// duration, reverse, class toggles and progress), without its 22KB UMD bundle that bundlers can't
// tree-shake. Same geometry: a scene starts where the trigger's top, plus `offset`, meets the line
// `hook` × viewport height down the window. A zero-duration scene is active from that point on; one
// with a duration reports progress 0–1 across it. `reverse: false` fires once and stops tracking.

const scenes = new Set();
let scheduled = false;
let bound = false;

function update() {
    scheduled = false;
    const scrollY = window.scrollY;
    const viewport = window.innerHeight;
    scenes.forEach((scene) => scene.update(scrollY, viewport));
}

function schedule() {
    if (scheduled) {
        return;
    }
    scheduled = true;
    // rAF never fires in a background tab; the next visible frame catches up anyway.
    requestAnimationFrame(update);
}

function bind() {
    if (bound) {
        return;
    }
    bound = true;
    window.addEventListener('scroll', schedule, {passive: true});
    window.addEventListener('resize', schedule);
    // Images and fonts move triggers after the first layout (ScrollMagic polled for this).
    window.addEventListener('load', schedule, {once: true});
}

function resolveElements(target) {
    if (!target) {
        return [];
    }
    if (typeof target === 'string') {
        return Array.from(document.querySelectorAll(target));
    }
    return target instanceof Element ? [target] : Array.from(target);
}

/**
 * @param {object} options
 * @param {string|Element} options.trigger - element whose top edge starts the scene
 * @param {number} [options.hook=0.5] - 0 = viewport top, 1 = viewport bottom
 * @param {number} [options.offset=0] - px added to the start position
 * @param {number|string} [options.duration=0] - px, or '100%' for one viewport height
 * @param {boolean} [options.reverse=true] - false: once active, stay active
 * @param {(active: boolean) => void} [options.onToggle]
 * @param {(progress: number) => void} [options.onProgress]
 */
export function scrollScene({trigger, hook = 0.5, offset = 0, duration = 0, reverse = true, onToggle, onProgress}) {
    const triggerElement = resolveElements(trigger)[0];
    if (!triggerElement) {
        return null;
    }

    let active = null;
    let progress = null;

    const scene = {
        update(scrollY, viewport) {
            const top = triggerElement.getBoundingClientRect().top + scrollY;
            const start = top + offset - Number(hook) * viewport;
            const length = typeof duration === 'string' && duration.endsWith('%')
                ? (parseFloat(duration) / 100) * viewport
                : Number(duration) || 0;

            const nextProgress = length > 0
                ? Math.min(1, Math.max(0, (scrollY - start) / length))
                : (scrollY >= start ? 1 : 0);
            const nextActive = length > 0
                ? scrollY >= start && scrollY <= start + length
                : scrollY >= start;

            if (nextProgress !== progress) {
                progress = nextProgress;
                onProgress?.(progress);
            }
            if (nextActive !== active) {
                // Starting out inactive is not a "leave": don't strip a class the markup ships with.
                const initialInactive = active === null && !nextActive;
                active = nextActive;
                if (!initialInactive) {
                    onToggle?.(active);
                }
                if (active && !reverse) {
                    scenes.delete(scene);
                }
            }
        },
    };

    scenes.add(scene);
    bind();
    scene.update(window.scrollY, window.innerHeight);
    return scene;
}

// onToggle handler for ScrollMagic's setClassToggle(target, className).
export function toggleClass(target, className) {
    return (active) => resolveElements(target).forEach((element) => element.classList.toggle(className, active));
}
